/**
 * Initializes the adjust data point tool in WPD.
 */
function init(wpd, wpdDocument, dataEntryVM) {
	// cannot pick and choose functions to override due to how it was written
	// completely overwrite the manual selection tool into a js class
	wpd.DeleteDataPointTool = class {
		constructor(axis, dataset) {
			this.axis = axis;
			this.dataset = dataset;

			this.resolvingOverlappingPixels = false;
			this._hoverDebounceTimer = null;
			this._disableHover = false;
		}

		static #$button = wpdDocument.getElementById("delete-point-button");
		static #cursorDistance = 25;

		onAttach() {
			// clear selected data points
			// prior data point selection is ignored by this tool
			this.dataset.unselectAll();

			wpd.DeleteDataPointTool.#$button.classList.add("pressed-button");
			wpd.graphicsWidget.setRepainter(
				new wpd.DataPointsRepainter(this.axis, this.dataset),
			);
		}

		onMouseClick(ev, pos, imagePos) {
			this._disableHover = true;
			const tupleCallback = (imagePos, index) => {
				let indexes = [];

				if (dataEntryVM.pointGroupOverhaul) {
					indexes = wpd.pointGroups.getTupleDataPointIndexes(
						this.dataset,
						this.dataset.getPixel(index),
					);

					// filter out center data points unless the clicked data point
					// is a center data point with other tuple data points in the other direction
					// conceptually, in a graph with point groups in both directions, each data
					// point has 2 tuples, one for each direction
					// this allows for the deletion of a data point in one direction without
					// affecting the other direction
					const dataPoint = this.dataset.getPixel(index);
					if (!wpd.pointGroups.isCenterDataPoint(this.axis, dataPoint)) {
						const direction = dataPoint.metadata.pointGroupAxisDirection;
						indexes = indexes.filter((i) => {
							const dp = this.dataset.getPixel(i);
							const isCenter = wpd.pointGroups.isCenterDataPoint(this.axis, dp);

							if (
								isCenter &&
								wpd.pointGroups.hasOtherTupleDataPoints(
									this.dataset,
									dp,
									direction,
								)
							) {
								return false;
							}
							return true;
						});
					}

					// sort indexes in descending order for removal
					const indexesDesc = [...indexes].sort((a, b) => b - a);

					// remove each data point in tuple
					indexesDesc.forEach((i) => {
						this.dataset.removePixelAtIndex(i);
					});
				} else {
					const tupleIndex = this.dataset.getTupleIndex(index);

					if (tupleIndex > -1) {
						const indexes = this.dataset.getTuple(tupleIndex);

						// sort indexes in descending order for removal
						const indexesDesc = [...indexes]
							.filter((i) => i !== null)
							.sort((a, b) => b - a);

						// remove each data point in tuple
						indexesDesc.forEach((idx) => {
							this.dataset.removePixelAtIndex(idx);
							// update pixel references in tuples
							this.dataset.refreshTuplesAfterPixelRemoval(idx);
						});

						// remove tuple
						this.dataset.removeTuple(tupleIndex);
					} else {
						// if tuple does not exist, just remove the pixel
						indexes = [this.dataset.removeNearestPixel(imagePos.x, imagePos.y)];
					}
				}

				finalCallback(indexes);
			};

			const pointCallback = async (imagePos) => {
				const nearestIndex = this.dataset.findNearestPixel(
					imagePos.x,
					imagePos.y,
					wpd.DeleteDataPointTool.#cursorDistance,
				);

				const indexes = [];
				const tagGroupIndexes = this.dataset
					.getTagGroupInstancePixelIndexes(nearestIndex)
					.sort((a, b) => b - a);
				const hasOtherData = tagGroupIndexes.find((index) => {
					if (index === nearestIndex) return false;
					const pixel = this.dataset.getPixel(index);
					if (!pixel || !pixel.metadata) return false;
					const isKeyTag = wpd.tagGroups.isKeyTag(pixel.metadata[0]?.tag?.name);
					return !isKeyTag;
				});

				const pixel = this.dataset.getPixel(nearestIndex);
				if (!pixel) return;

				const isKeyTagGroup = wpd.tagGroups.isKeyTagGroup(
					pixel.metadata?.tagGroup?.name,
				);

				if (hasOtherData || isKeyTagGroup) {
					// only resolve overlapping pixels if user selected a single pixel
					// and we are not already resolving overlapping pixels
					if (
						dataEntryVM.viewEditOverlappingDataPoints &&
						!this.resolvingOverlappingPixels
					) {
						// check for overlapping pixels for each pixel index
						const overlappingPixels =
							this.dataset.findOverlappingPixels(nearestIndex);

						// if there are overlapping pixels, open the metadata select dialog
						if (overlappingPixels.length > 1) {
							this.resolvingOverlappingPixels = true;

							const selectedIndex = await wpd.custom.openMetadataSelectDialog(
								overlappingPixels,
							);

							if (selectedIndex !== undefined) {
								this.dataset.removePixelAtIndex(selectedIndex);
								indexes.push(selectedIndex);
							}

							this.resolvingOverlappingPixels = false;
						} else {
							this.dataset.removePixelAtIndex(nearestIndex);
							indexes.push(nearestIndex);
						}
					} else {
						this.dataset.removeNearestPixel(imagePos.x, imagePos.y);
						indexes.push(nearestIndex);
					}
				} else {
					tagGroupIndexes.forEach((index) => {
						this.dataset.removePixelAtIndex(index);
						indexes.push(index);
					});
				}

				if (!dataEntryVM.pointGroupOverhaul) {
					// remove data point index references from tuples
					const tupleIndex = this.dataset.getTupleIndex(nearestIndex);

					if (tupleIndex > -1) {
						this.dataset.removeFromTupleAt(tupleIndex, nearestIndex);

						// update pixel references in tuples
						this.dataset.refreshTuplesAfterPixelRemoval(nearestIndex);

						// remove tuple if no point index references left in tuple
						if (this.dataset.isTupleEmpty(tupleIndex)) {
							this.dataset.removeTuple(tupleIndex);
						}
					}
				}

				finalCallback(indexes);
			};

			const finalCallback = async (indexes) => {
				wpd.graphicsWidget.resetData();
				wpd.graphicsWidget.forceHandlerRepaint();
				wpd.graphicsWidget.updateZoomOnEvent(ev);
				wpd.dataPointCounter.setCount(this.dataset.getCount());

				// dispatch point delete event
				indexes.forEach((index) => {
					wpd.events.dispatch("wpd.dataset.point.delete", {
						axes: this.axis,
						dataset: this.dataset,
						index: index,
					});
				});

				if (indexes.length > 0) {
					this.#resetHover();
					await dataEntryVM.autoSaveMetadata();
				}

				this._disableHover = false;
			};

			// handle point tuple deletion
			if (this.dataset.hasPointGroups()) {
				const index = this.dataset.findNearestPixel(
					imagePos.x,
					imagePos.y,
					wpd.DeleteDataPointTool.#cursorDistance,
				);

				if (index > -1) {
					wpd.events.dispatch("wpd.dataset.point.closeHover");
					this._disableHover = true;

					// display tuple deletion confirmation popup if point groups exist
					// only show tuple delete popup if selected point is a center data point
					const dataPoint = this.dataset.getPixel(index);
					if (
						wpd.pointGroups.isCenterDataPoint(this.axis, dataPoint) &&
						wpd.pointGroups.hasTupleDataPoints(this.dataset, dataPoint)
					) {
						wpd.pointGroups.showDeleteTuplePopup(
							(...args) => {
								this._disableHover = false;
								tupleCallback.bind(this, imagePos, index)(...args);
							},
							() => {
								this._disableHover = false;
							},
						);
					} else {
						pointCallback(imagePos);
					}
				}
			} else {
				pointCallback(imagePos);
			}
		}

		onKeyDown(ev) {
			if (wpd.acquireData.isToolSwitchKey(ev.keyCode)) {
				wpd.acquireData.switchToolOnKeyPress(
					String.fromCharCode(ev.keyCode).toLowerCase(),
				);
			}
		}

		onRemove() {
			// clear selected data points
			this.dataset.unselectAll();

			wpd.graphicsWidget.forceHandlerRepaint();

			wpd.DeleteDataPointTool.#$button.classList.remove("pressed-button");
		}

		onMouseMove(ev, pos, imagePos) {
			const pxIndex = this.dataset.findNearestPixel(
				imagePos.x,
				imagePos.y,
				wpd.DeleteDataPointTool.#cursorDistance,
			);

			if (dataEntryVM.viewEditOverlappingDataPoints) {
				if (this.resolvingOverlappingPixels) {
					return;
				}

				// clear existing debounce timer
				if (this._hoverDebounceTimer) {
					clearTimeout(this._hoverDebounceTimer);
					this._hoverDebounceTimer = null;
				}

				if (pxIndex > -1) {
					const foundPoints = [];
					// only display overlapping pixels for text data
					if (wpd.custom.identifyAxis(this.axis) === "image") {
						foundPoints.push(...this.dataset.findOverlappingPixels(pxIndex));
					} else {
						foundPoints.push({
							pixel: this.dataset.getPixel(pxIndex),
							index: pxIndex,
						});
					}
					this.#onHoverV2(ev, foundPoints);
				} else {
					this.#resetHover();
				}
			} else {
				let foundPoint = null;
				if (pxIndex > -1) {
					foundPoint = {
						pixel: this.dataset.getPixel(pxIndex),
						index: pxIndex,
					};
				}

				this.#resetHover();

				if (foundPoint) {
					this.#onHover(ev, foundPoint);
				}
			}
		}

		#onHover(ev, point) {
			var pixel = point.pixel;
			if (!pixel) return;

			var index = point.index;
			var screenPx = wpd.graphicsWidget.screenPx(pixel.x, pixel.y),
				ctx = wpd.graphicsWidget.getAllContexts();

			ctx.hoverCtx.beginPath();
			ctx.hoverCtx.strokeStyle = "rgb(240, 157, 55)";
			ctx.hoverCtx.lineWidth = 2;
			ctx.hoverCtx.arc(screenPx.x, screenPx.y, 4, 0, 2.0 * Math.PI, true);
			ctx.hoverCtx.stroke();
			if (this._disableHover) return;
			wpd.events.dispatch("wpd.dataset.point.hover", {
				axes: this.axis,
				dataset: this.dataset,
				index: index,
			});
		}

		#onHoverV2(ev, points) {
			if (points.length === 0) {
				return;
			}

			const pixel = points[0].pixel;
			this.#drawDataPointHighlight(pixel);

			// debounce hover with 200ms delay
			this._hoverDebounceTimer = setTimeout(() => {
				if (this._disableHover) return;

				this.#resetHover();
				this.#drawDataPointHighlight(pixel);

				wpd.events.dispatch("wpd.dataset.point.hover", {
					axes: this.axis,
					dataset: this.dataset,
					indexes: points.map(({ index }) => index),
				});

				this._hoverDebounceTimer = null;
			}, 200);
		}

		#drawDataPointHighlight(pixel) {
			if (!pixel) {
				return;
			}

			const screenPx = wpd.graphicsWidget.screenPx(pixel.x, pixel.y);
			const ctx = wpd.graphicsWidget.getAllContexts();

			ctx.hoverCtx.beginPath();
			ctx.hoverCtx.strokeStyle = "rgb(240, 157, 55)";
			ctx.hoverCtx.lineWidth = 2;
			ctx.hoverCtx.arc(screenPx.x, screenPx.y, 4, 0, 2.0 * Math.PI, true);
			ctx.hoverCtx.stroke();
		}

		#resetHover() {
			const ctx = wpd.graphicsWidget.getAllContexts();
			let canvasDims = wpd.graphicsWidget.screenPx(
				wpd.graphicsWidget.originalWidth,
				wpd.graphicsWidget.originalHeight,
			);
			ctx.hoverCtx.clearRect(0, 0, canvasDims.x, canvasDims.y);
			wpd.events.dispatch("wpd.dataset.point.closeHover");
		}
	};
}

export { init };
